import {
  Button,
  SidebarContextWrapper,
  useIsMobile,
  useSidebar,
} from "@/components/ui";
import { PanelLeft } from "lucide-react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
} from "@/components/ui/breadcrumb";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ThreadRenameDialog } from "@/features/sidebar/projects/dialogs/ThreadRenameDialog";
import { useToast } from "@/hooks";
import { SupabaseThreadUtils } from "@/lib/db/utils/supabase/threads";
import { SupabaseWorkspaceUtils } from "@/lib/db/utils/supabase/workspace";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/utils";
import {
  deleteThreadWithQueue,
  isThreadBeingDeleted,
} from "@/lib/utils/threadDeletionQueue";
import { useAssistantStore, useAuthStore, useThreadStore } from "@/store";
import { useWorkspaceThreadsStore } from "@/store/workspaceThreadsStore";
import { useChatMove } from "@/features/sidebar/projects/hooks/useChatMove";
import { eventBus } from "@/utils/eventBus";
import { AnimatePresence, motion } from "framer-motion";
import PropTypes from "prop-types";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { getWorkspaceIconComponent } from "@/lib/constants/workspace-customization";
import { AssistantSelector } from "./AssistantSelector";
import { ModelSwitcher } from "./ModelSwitcher";
import { RunSettingsPanel } from "./RunSettingsPanel";
import { ThreadMenu } from "./ThreadMenu";

const AssistantOption = memo(({ assistant, onSelect }) => (
  <button
    onClick={() => onSelect(assistant)}
    className={cn(
      "flex items-center gap-3 p-3 w-full",
      "hover:bg-sidebar-accent/30",
      "rounded-lg",
      "transition-all duration-200",
      "bg-transparent",
      "relative group",
      "text-white",
      "border-2 border-purple-500/[0.15]",
      "hover:border-purple-500/50"
    )}
  >
    <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 shrink-0" />
    <div className="flex flex-col items-start">
      <span className="text-white font-medium">{assistant.name}</span>
      <span className="text-xs text-zinc-400">{assistant.description}</span>
    </div>
  </button>
));

AssistantOption.displayName = "AssistantOption";

AssistantOption.propTypes = {
  assistant: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
  }),
  onSelect: PropTypes.func,
};

export const NavigationActions = memo(
  ({ showMenuButton: _showMenuButton, onToggleContextPanel: _onToggleContextPanel, onAssistantSelect, className, isCanvas = false }) => {
    const { currentAssistant: _currentAssistant } = useAssistantStore();
    const { open: _open } = useSidebar();
    const { toast } = useToast();
    const navigate = useNavigate();
    const [isRunSettingsOpen, setIsRunSettingsOpen] = useState(false);

    // Get current thread from store
    const { currentThread, currentThreadId, threads: _threads } = useThreadStore();
    const { workspaces, setWorkspaces, user } = useAuthStore();
    const { workspaceThreads, setWorkspaceThreads } = useWorkspaceThreadsStore();
    const { handleChatMove: moveChatViaRpc } = useChatMove({
      workspaces,
      setWorkspaces,
      workspaceThreads,
      setWorkspaceThreads,
    });

    // Thread menu state
    const [isDeleting, setIsDeleting] = useState(false);
    const [renameDialogOpen, setRenameDialogOpen] = useState(false);
    const [newThreadName, setNewThreadName] = useState("");
    const [isRenaming, setIsRenaming] = useState(false);

    // Determine if current thread is a project thread
    const isProjectThread = currentThread?.workspace_id != null;

    // Listen for the open-assistant-settings event
    useEffect(() => {
      const handleOpenAssistantSettings = (assistant) => {
        if (assistant) {
          useAssistantStore.getState().setCurrentAssistant(assistant);
        }

        // Ensure we're not in the middle of another state update
        setTimeout(() => {
          setIsRunSettingsOpen(true);
        }, 100);
      };

      eventBus.on("open-assistant-settings", handleOpenAssistantSettings);

      return () => {
        eventBus.off("open-assistant-settings", handleOpenAssistantSettings);
      };
    }, []);

    // Prevent panel from closing immediately after opening
    useEffect(() => {
      let _ignoreCloseEvents = false;

      if (isRunSettingsOpen) {
        _ignoreCloseEvents = true;

        // Add a brief delay to prevent immediate closing
        const timer = setTimeout(() => {
          _ignoreCloseEvents = false;
        }, 300);

        return () => clearTimeout(timer);
      }

      return () => {};
    }, [isRunSettingsOpen]);

    // Thread action handlers
    const handleToggleBookmark = useCallback(
      async (thread) => {
        try {
          const isBookmarked =
            thread.metadata?.is_bookmarked || thread.metadata?.bookmarked;
          const updatedMetadata = {
            ...(thread.metadata || {}),
            is_bookmarked: !isBookmarked,
            bookmarked: !isBookmarked,
            bookmark_updated_at: new Date().toISOString(),
          };

          await SupabaseThreadUtils.updateThreadMetadata({
            supabaseThreadId: thread.id,
            metadata: updatedMetadata,
          });

          useThreadStore.setState((state) => ({
            currentThread:
              state.currentThread?.id === thread.id
                ? { ...state.currentThread, metadata: updatedMetadata }
                : state.currentThread,
            threads: state.threads.map((t) =>
              t.id === thread.id ? { ...t, metadata: updatedMetadata } : t
            ),
          }));

          toast({
            title: !isBookmarked ? "Chat pinned" : "Chat unpinned",
            description: !isBookmarked
              ? "Chat has been pinned to the top"
              : "Chat has been unpinned",
          });
        } catch (error) {
          console.error("Error toggling bookmark:", error);
          toast({
            title: "Error",
            description: "Failed to update pin status",
            variant: "destructive",
          });
        }
      },
      [toast]
    );

    const handleRename = useCallback((thread) => {
      setNewThreadName(thread.title || "");
      setRenameDialogOpen(true);
    }, []);

    const handleRenameSubmit = useCallback(async () => {
      if (!currentThread || !newThreadName.trim()) return;

      try {
        setIsRenaming(true);

        await supabase
          .from("threads")
          .update({
            title: newThreadName.trim(),
            updated_at: new Date().toISOString(),
          })
          .eq("id", currentThread.id);

        useThreadStore.setState((state) => ({
          currentThread:
            state.currentThread?.id === currentThread.id
              ? { ...state.currentThread, title: newThreadName.trim() }
              : state.currentThread,
          currentThreadTitle: newThreadName.trim(),
          threads: state.threads.map((t) =>
            t.id === currentThread.id
              ? { ...t, title: newThreadName.trim() }
              : t
          ),
        }));

        setRenameDialogOpen(false);
        setNewThreadName("");

        toast({
          title: "Chat renamed",
          description: "Chat name has been updated",
        });
      } catch (error) {
        console.error("Error renaming thread:", error);
        toast({
          title: "Error",
          description: "Failed to rename chat",
          variant: "destructive",
        });
      } finally {
        setIsRenaming(false);
      }
    }, [currentThread, newThreadName, toast]);

    const handleRenameCancel = useCallback(() => {
      setRenameDialogOpen(false);
      setNewThreadName("");
    }, []);

    const handleClone = useCallback(
      async (thread) => {
        try {
          if (!user?.id) {
            toast({
              title: "Error",
              description: "You must be logged in to clone a chat",
              variant: "destructive",
            });
            return;
          }

          const workspaceId = thread.workspace_id;

          // Fetch original thread data
          const { data: originalThread, error: threadError } = await supabase
            .from("threads")
            .select("*")
            .eq("id", thread.id)
            .single();

          if (threadError) throw threadError;

          // Fetch messages
          const { data: originalMessages, error: messagesError } = await supabase
            .from("thread_messages")
            .select("*")
            .eq("thread_id", thread.id)
            .order("created_at", { ascending: true });

          if (messagesError) throw messagesError;

          const newThreadTitle = `Clone of ${thread.title || "Untitled Chat"}`;
          const timestamp = new Date().toISOString();

          const threadMetadata = {
            ...originalThread.metadata,
            cloned_from: thread.id,
            cloned_at: timestamp,
            original_title: thread.title,
            user_id: user.id,
            workspace_id: workspaceId,
          };

          // Create new thread
          const { data: newThread, error: newThreadError } = await supabase
            .from("threads")
            .insert({
              title: newThreadTitle,
              user_id: user.id,
              workspace_id: workspaceId,
              metadata: threadMetadata,
              created_at: timestamp,
              updated_at: timestamp,
              provider: originalThread.provider || "openai",
              status: "active",
            })
            .select("*")
            .single();

          if (newThreadError) throw newThreadError;

          // Clone messages
          if (originalMessages && originalMessages.length > 0) {
            const messagesToInsert = originalMessages.map((message) => ({
              thread_id: newThread.id,
              message_id: `msg_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`,
              role: message.role,
              content: message.content,
              metadata: message.metadata || {},
              created_at: timestamp,
              workspace_id: workspaceId,
              user_id: user.id,
              provider: message.provider || "openai",
              file_ids: message.file_ids || [],
              tools_count: message.tools_count || 0,
              attachments: message.attachments,
              annotations: message.annotations,
              tool_calls: message.tool_calls,
            }));

            await supabase.from("thread_messages").insert(messagesToInsert);
          }

          // Add to workspace if it's a project thread
          if (workspaceId) {
            await SupabaseWorkspaceUtils.addThreadToWorkspace(
              workspaceId,
              String(newThread.id)
            );
          }

          useThreadStore.setState((state) => ({
            threads: [newThread, ...state.threads],
          }));

          toast({
            title: "Success",
            description: "Chat cloned successfully",
          });

          navigate(`/chat/${newThread.id}`);
        } catch (error) {
          console.error("Error cloning thread:", error);
          toast({
            title: "Error",
            description: "Failed to clone chat",
            variant: "destructive",
          });
        }
      },
      [user, navigate, toast]
    );

    const handleDelete = useCallback(
      async (thread) => {
        if (isThreadBeingDeleted(thread.id)) return;

        setIsDeleting(true);

        const workspaceId = thread.workspace_id;

        const success = await deleteThreadWithQueue(thread.id, {
          isCurrentThread: currentThreadId === thread.id,
          workspaceId,
          vectorStoreId: thread.metadata?.vector_store_id,
          originalThread: thread,
          navigate: currentThreadId === thread.id ? navigate : null,
          showErrorToast: (message) => {
            toast({
              title: "Error",
              description: message,
              variant: "destructive",
            });
          },
          onSuccess: () => {
            setIsDeleting(false);
            toast({
              title: "Chat deleted",
              description: "The chat has been deleted",
            });
          },
          onError: () => {
            setIsDeleting(false);
            toast({
              title: "Error",
              description: "Failed to delete chat",
              variant: "destructive",
            });
          },
        });

        if (!success) {
          setIsDeleting(false);
        }
      },
      [currentThreadId, navigate, toast]
    );

    const handleMoveToProject = useCallback(
      async (thread, targetWorkspaceId) => {
        // Atomic move via the canonical helper: updates the workspace arrays
        // (which drives the sidebar's signature-based refetch), the thread
        // store, optimistic state + rollback, and the toast.
        const moved = await moveChatViaRpc(
          thread,
          targetWorkspaceId,
          thread.workspace_id
        );
        if (!moved) return;

        // useChatMove updates the threads list but not currentThread; keep the
        // chat-page header (breadcrumb + move menu) in sync.
        useThreadStore.setState((state) =>
          state.currentThread?.id === thread.id
            ? {
                currentThread: {
                  ...state.currentThread,
                  workspace_id: targetWorkspaceId,
                },
              }
            : {}
        );
      },
      [moveChatViaRpc]
    );

    return (
      <div className={cn("flex items-center gap-1", className)}>
        <AssistantSelector
          onAssistantSelect={onAssistantSelect}
          compact={true}
          hideText={isCanvas}
        />

        {/* Thread Menu - only show when there's a current thread */}
        {currentThread && (
          <ThreadMenu
            thread={currentThread}
            workspaces={workspaces}
            isProjectThread={isProjectThread}
            isDeleting={isDeleting || isThreadBeingDeleted(currentThread?.id)}
            showBookmark={!isProjectThread}
            usePinTerminology={true}
            onToggleBookmark={handleToggleBookmark}
            onRename={handleRename}
            onMoveToProject={handleMoveToProject}
            onMoveToChats={isProjectThread ? (thread) => handleMoveToProject(thread, null) : undefined}
            onClone={handleClone}
            onDelete={handleDelete}
          />
        )}

        {/* Render RunSettingsPanel without visible trigger - controlled via event bus */}
        <RunSettingsPanel
          open={isRunSettingsOpen}
          onClose={(isOpen) => setIsRunSettingsOpen(isOpen)}
          hideTrigger={true}
        />

        {/* Rename Dialog */}
        <ThreadRenameDialog
          isOpen={renameDialogOpen}
          onOpenChange={setRenameDialogOpen}
          threadToRename={currentThread}
          newThreadName={newThreadName}
          setNewThreadName={setNewThreadName}
          isRenaming={isRenaming}
          onRename={handleRenameSubmit}
          onCancel={handleRenameCancel}
        />
      </div>
    );
  }
);

NavigationActions.displayName = "NavigationActions";

NavigationActions.propTypes = {
  showMenuButton: PropTypes.bool,
  onToggleContextPanel: PropTypes.func,
  onAssistantSelect: PropTypes.func,
  className: PropTypes.string,
  isCanvas: PropTypes.bool,
};

export const NavigationHeader = memo(({ onToggleContextPanel, className, isCanvas = false }) => {
  const { toast } = useToast();
  const { open: _open, toggleSidebar } = useSidebar();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [showMenuButton, setShowMenuButton] = useState(false);
  const [_lastScrollY, setLastScrollY] = useState(0);
  const [_isCreatingChat, setIsCreatingChat] = useState(false);
  const [showAssistantDialog, setShowAssistantDialog] = useState(false);

  const {
    currentAssistant,
    assistants,
    setCurrentAssistant,
    currentAssistantId: _currentAssistantId,
    setCurrentAssistantId,
  } = useAssistantStore();
  const { currentWorkspace, user, workspaces } = useAuthStore();
  const { currentThread, currentThreadTitle: _currentThreadTitle, currentThreadId: _currentThreadId } =
    useThreadStore();
  const { threads, setThreads, clearMessages } = useThreadStore();

  const createThread = useThreadStore((state) => state.createThread);
  const _clearCurrentThread = useThreadStore(
    (state) => state.clearCurrentThread
  );
  const setCurrentThread = useThreadStore((state) => state.setCurrentThread);
  const setCurrentThreadId = useThreadStore(
    (state) => state.setCurrentThreadId
  );
  const setCurrentThreadTitle = useThreadStore(
    (state) => state.setCurrentThreadTitle
  );

  // Get project info if current thread belongs to a project
  const threadProjectId = currentThread?.workspace_id;
  const threadProject = threadProjectId
    ? workspaces?.find((w) => w.id === threadProjectId)
    : null;

  const isMounted = useRef(true);
  const navigatingRef = useRef(false);

  // Check if we're on a tool route
  const isToolRoute = location.pathname.includes("/chat/tool/");

  // Check if we're on the chat/new route
  const _isNewChatRoute = location.pathname === "/chat/new";

  // Component cleanup
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const mainContent = document.querySelector("main");
    if (!mainContent) return;

    const handleScroll = () => {
      const currentScrollY = mainContent.scrollTop;
      if (currentScrollY > 100) {
        setShowMenuButton(true);
      } else {
        setShowMenuButton(false);
      }
      setLastScrollY(currentScrollY);
    };

    mainContent.addEventListener("scroll", handleScroll, { passive: true });
    return () => mainContent.removeEventListener("scroll", handleScroll);
  }, []);

  const handleSelectAssistant = async (assistant) => {
    setCurrentAssistant(assistant);
    setCurrentAssistantId(assistant.id);
    setShowAssistantDialog(false);
    await createNewThread(assistant.id);
  };

  const createNewThread = async (assistantId) => {
    try {
      if (!user?.id) {
        throw new Error("No user logged in");
      }

      // Set navigating state to prevent multiple operations
      if (navigatingRef.current) return;
      navigatingRef.current = true;

      // Navigate to /chat/new first for immediate feedback
      navigate("/chat/new");

      // CRITICAL FIX: Pass user.id as userId, not currentWorkspace.id
      // For regular chats, don't pass workspace information in metadata
      const newThread = await createThread(user.id, assistantId);

      // Check if component is still mounted before updating state
      if (!isMounted.current) return;

      clearMessages();
      setCurrentThread(newThread);
      setCurrentThreadId(newThread.id);
      setCurrentThreadTitle(newThread.title || "New Chat");
      setThreads([newThread, ...threads]);

      // Add a slight delay to avoid animation conflicts
      setTimeout(() => {
        if (isMounted.current) {
          navigate(`/chat/${newThread.id}`);
        }
        navigatingRef.current = false;
      }, 100);
    } catch (error) {
      navigatingRef.current = false;
      console.error("Failed to create new chat:", error);
      // toast({
      //   title: "Failed to create chat",
      //   description: error.message,
      //   variant: "destructive",
      // });
    } finally {
      if (isMounted.current) {
        setIsCreatingChat(false);
      }
    }
  };

  const _handleOpenAssistantSheet = () => {
    if (currentAssistant) {
      eventBus.emit("open-assistant-sheet", currentAssistant);
    }
  };

  const handleAssistantSelect = async (assistant) => {
    if (assistant && currentWorkspace) {
      try {
        // Update the current assistant without creating a thread
        // Thread will be created when user sends their first message
        setCurrentAssistant(assistant);
        setCurrentAssistantId(assistant.id);
      } catch (error) {
        console.error("Error selecting assistant:", error);
        toast({
          title: "Error",
          description: "Failed to select assistant",
          variant: "destructive",
        });
      }
    }
  };

  return (
    <>
      <Dialog open={showAssistantDialog} onOpenChange={setShowAssistantDialog}>
        <DialogContent className="max-w-[95vw] md:max-w-2xl bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle>Choose an Assistant</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Select an assistant to start your conversation with
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 mt-4">
            {assistants?.map((assistant) => (
              <AssistantOption
                key={assistant.id}
                assistant={assistant}
                onSelect={handleSelectAssistant}
              />
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <motion.div
        className={cn(
          "flex items-center justify-between",
          !isCanvas && "flex-1",
          "relative z-50",
          isCanvas
            ? "p-3 border-b border-border bg-background"
            : cn("px-1 sm:px-2", "pt-1 sm:pt-0", "pl-2 sm:pl-1", "bg-transparent"),
          isToolRoute && "hidden",
          className
        )}
        initial={false}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key="navigation-content"
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            className={cn(
              "transition-opacity duration-200",
              "flex-1 min-w-0",
              "bg-transparent",
              isMobile ? "max-w-[90%]" : ""
            )}
          >
            <motion.div
              className={cn("overflow-hidden")}
              animate={{
                height: "auto",
                opacity: 1,
              }}
              initial={{ height: "auto", opacity: 1 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
            >
              <Breadcrumb
                className={cn(
                  "min-w-0",
                  "block" // Show on all devices
                )}
              >
                <BreadcrumbList className="flex-nowrap gap-1">
                  {/* Mobile sidebar toggle - only visible on narrow screens */}
                  {isMobile && (
                    <BreadcrumbItem className="flex-shrink-0 relative z-[200]">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 rounded-full"
                        onClick={toggleSidebar}
                        aria-label="Toggle sidebar"
                      >
                        <PanelLeft className="h-4 w-4" />
                      </Button>
                    </BreadcrumbItem>
                  )}
                  {/* Project icon - visible when thread belongs to a project */}
                  {threadProject && (
                    <BreadcrumbItem className="flex-shrink-0">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Link
                              to={`/projects/${threadProject.id}`}
                              className="flex items-center justify-center h-7 w-7 rounded-full hover:bg-accent transition-colors"
                            >
                              {(() => {
                                const IconComponent = getWorkspaceIconComponent(
                                  threadProject.metadata?.icon
                                );
                                return (
                                  <IconComponent
                                    className="h-4 w-4"
                                    style={{
                                      color:
                                        threadProject.metadata?.color ||
                                        "currentColor",
                                    }}
                                  />
                                );
                              })()}
                            </Link>
                          </TooltipTrigger>
                          <TooltipContent side="bottom" className={cn("z-[310]", isCanvas && "z-[100001]")}>
                            <p>{threadProject.name}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </BreadcrumbItem>
                  )}
                  {user && (
                    <BreadcrumbItem className="flex-shrink min-w-0">
                      <ModelSwitcher
                        className="h-7 sm:h-8 min-w-0"
                        compact={true}
                      />
                    </BreadcrumbItem>
                  )}
                  <div
                    className={cn(
                      "hidden sm:flex items-center min-w-0", // Hide on mobile, show on sm and up
                      "flex-1 md:flex-initial"
                    )}
                  >
                    {/* Thread title section removed to declutter the top bar */}
                  </div>
                </BreadcrumbList>
              </Breadcrumb>
            </motion.div>
          </motion.div>
        </AnimatePresence>

        <NavigationActions
          showMenuButton={showMenuButton}
          onToggleContextPanel={onToggleContextPanel}
          onAssistantSelect={handleAssistantSelect}
          isCanvas={isCanvas}
          className={cn(
            "transition-all duration-300",
            "bg-transparent",
            isMobile ? "ml-1" : "ml-4",
            isToolRoute && "hidden"
          )}
        />
      </motion.div>
    </>
  );
});

NavigationHeader.displayName = "NavigationHeader";

NavigationHeader.propTypes = {
  onToggleContextPanel: PropTypes.func,
  className: PropTypes.string,
  isCanvas: PropTypes.bool,
};

export default function WrappedNavigationHeader(props) {
  return (
    <SidebarContextWrapper>
      <NavigationHeader {...props} />
    </SidebarContextWrapper>
  );
}
